import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Package, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

const PAIRS = [
  'BTC-USD', 'ETH-USD', 'SOL-USD', 'LTC-USD', 'DOGE-USD',
  'BCH-USD', 'XRP-USD', 'SHIB-USD'
];

const ManualBuyLotsSection = () => {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ pair: 'BTC-USD', quantity: '', buy_price: '', date: '' });
  const [deleting, setDeleting] = useState(null);

  const fetchLots = useCallback(async () => {
    try {
      const res = await axios.get(`${API_V2}/user/buy-lots`, { withCredentials: true });
      if (res.data.success) setLots(res.data.lots);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLots(); }, [fetchLots]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.quantity || !form.buy_price) {
      toast.error('Quantity and buy price are required');
      return;
    }
    setAdding(true);
    try {
      const res = await axios.post(`${API_V2}/user/buy-lots`, {
        pair: form.pair,
        quantity: parseFloat(form.quantity),
        buy_price: parseFloat(form.buy_price),
        date: form.date || undefined,
      }, { withCredentials: true });
      if (res.data.success) {
        toast.success(`Added ${form.quantity} ${form.pair.split('-')[0]} lot`);
        setForm({ pair: 'BTC-USD', quantity: '', buy_price: '', date: '' });
        setShowForm(false);
        fetchLots();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add lot');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (lotId) => {
    setDeleting(lotId);
    try {
      await axios.delete(`${API_V2}/user/buy-lots/${lotId}`, { withCredentials: true });
      toast.success('Buy lot removed');
      fetchLots();
    } catch {
      toast.error('Failed to delete lot');
    } finally {
      setDeleting(null);
    }
  };

  const totalValue = lots.reduce((s, l) => s + (l.usd_value || 0), 0);

  return (
    <Card className="matrix-card rounded-none" data-testid="buy-lots-card">
      <CardHeader className="border-b border-[#003B00] p-4">
        <CardTitle className="text-sm uppercase tracking-widest flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4" strokeWidth={1.5} />
            PRE-OWNED ASSETS
            {lots.length > 0 && (
              <span className="text-xs text-gray-500 font-normal ml-2">
                {lots.length} lot{lots.length !== 1 ? 's' : ''} | ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </div>
          <Button
            size="sm"
            onClick={() => setShowForm(!showForm)}
            className="bg-[#003B00] hover:bg-[#004d00] text-[#00FF41] text-xs h-7 px-3"
            data-testid="add-buy-lot-btn"
          >
            <Plus className="h-3 w-3 mr-1" /> Add Lot
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {/* Add Form */}
        {showForm && (
          <form onSubmit={handleAdd} className="border border-[#003B00] p-3 space-y-3" data-testid="buy-lot-form">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div>
                <label className="text-[10px] text-gray-500 uppercase block mb-1">Coin</label>
                <select
                  value={form.pair}
                  onChange={(e) => setForm({ ...form, pair: e.target.value })}
                  className="w-full bg-black border border-[#003B00] text-[#00FF41] text-xs p-2 focus:outline-none focus:border-[#00FF41]"
                  data-testid="lot-pair-select"
                >
                  {PAIRS.map(p => <option key={p} value={p}>{p.split('-')[0]}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 uppercase block mb-1">Quantity</label>
                <Input
                  type="number"
                  step="any"
                  placeholder="0.00"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="bg-black border-[#003B00] text-[#00FF41] text-xs h-9"
                  data-testid="lot-quantity-input"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 uppercase block mb-1">Buy Price (USD)</label>
                <Input
                  type="number"
                  step="any"
                  placeholder="0.00"
                  value={form.buy_price}
                  onChange={(e) => setForm({ ...form, buy_price: e.target.value })}
                  className="bg-black border-[#003B00] text-[#00FF41] text-xs h-9"
                  data-testid="lot-price-input"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 uppercase block mb-1">Date Bought</label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="bg-black border-[#003B00] text-[#00FF41] text-xs h-9"
                  data-testid="lot-date-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                disabled={adding}
                className="bg-[#00FF41] text-black hover:bg-[#00cc33] text-xs h-7 px-4"
                data-testid="lot-submit-btn"
              >
                {adding ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Add Buy Lot
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowForm(false)}
                className="text-gray-500 hover:text-gray-300 text-xs h-7"
              >
                Cancel
              </Button>
              {form.quantity && form.buy_price && (
                <span className="text-[10px] text-gray-500 ml-auto">
                  Total: ${(parseFloat(form.quantity || 0) * parseFloat(form.buy_price || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
            </div>
          </form>
        )}

        {/* Lots Table */}
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 text-[#00FF41] animate-spin" />
          </div>
        ) : lots.length === 0 ? (
          <div className="text-center py-4 text-gray-600 text-xs">
            No pre-owned assets defined. Click "Add Lot" to enter your existing crypto holdings for accurate P/L tracking.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="buy-lots-table">
              <thead>
                <tr className="border-b border-[#003B00] text-gray-500">
                  <th className="text-left p-2">COIN</th>
                  <th className="text-right p-2">QTY</th>
                  <th className="text-right p-2">BUY PRICE</th>
                  <th className="text-right p-2">TOTAL USD</th>
                  <th className="text-right p-2">DATE</th>
                  <th className="text-right p-2"></th>
                </tr>
              </thead>
              <tbody>
                {lots.map((lot) => (
                  <tr key={lot.lot_id} className="border-b border-[#003B00]/30 hover:bg-[#001a00]" data-testid={`lot-row-${lot.lot_id}`}>
                    <td className="p-2 text-[#00FF41] font-bold">{lot.pair?.split('-')[0] || lot.coin?.toUpperCase()}</td>
                    <td className="p-2 text-right font-mono text-gray-300">{lot.quantity?.toFixed(8)}</td>
                    <td className="p-2 text-right font-mono text-gray-300">${lot.buy_price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="p-2 text-right font-mono text-[#00FF41]">${lot.usd_value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="p-2 text-right text-gray-500">{lot.date || '-'}</td>
                    <td className="p-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(lot.lot_id)}
                        disabled={deleting === lot.lot_id}
                        className="h-6 w-6 p-0 text-red-500/50 hover:text-red-500 hover:bg-red-950/20"
                        data-testid={`lot-delete-${lot.lot_id}`}
                      >
                        {deleting === lot.lot_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ManualBuyLotsSection;
